/**
 * Centralized query key factory for TanStack Query.
 *
 * Provides type-safe, hierarchical query keys with automatic invalidation support.
 * Each domain follows a consistent pattern:
 * - `all` - Base key for all queries in this domain
 * - `lists()` - Base key for list queries
 * - `list(filters)` - Specific list query with optional filters
 * - `details()` - Base key for detail queries
 * - `detail(id)` - Specific detail query by ID
 *
 * @example
 * // Query
 * useQuery({ queryKey: queryKeys.companies.detail(companyId), ... })
 *
 * @example
 * // Invalidate all company queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.companies.all })
 *
 * @example
 * // Invalidate specific company detail
 * queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail(companyId) })
 */

export const queryKeys = {
  /**
   * Company domain - Companies, sites, units, personnel
   */
  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    list: () => [...queryKeys.companies.lists()] as const,
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.companies.details(), id] as const,
    personnel: (companyId: string) => [...queryKeys.companies.all, companyId, 'personnel'] as const,
  },

  /**
   * Site domain - Sites (stabilimenti)
   */
  sites: {
    all: ['sites'] as const,
    byCompany: (companyId: string) => [...queryKeys.sites.all, 'company', companyId] as const,
    detail: (id: string) => [...queryKeys.sites.all, id] as const,
    units: (siteId: string) => [...queryKeys.sites.all, siteId, 'units'] as const,
  },

  /**
   * Refrigeration unit domain
   */
  units: {
    all: ['units'] as const,
    bySite: (siteId: string) => [...queryKeys.units.all, 'site', siteId] as const,
    detail: (id: string) => [...queryKeys.units.all, id] as const,
  },

  /**
   * Report domain - Inspection reports
   */
  reports: {
    all: ['reports'] as const,
    lists: () => [...queryKeys.reports.all, 'list'] as const,
    byCompany: (companyId: string) => [...queryKeys.reports.all, companyId] as const,
    bySite: (siteId: string) => [...queryKeys.reports.all, 'site', siteId] as const,
    latestFinalizedBySite: (siteId: string) =>
      [...queryKeys.reports.bySite(siteId), 'latest-finalized'] as const,
    detail: (id: string) => [...queryKeys.reports.all, 'detail', id] as const,
    view: (id: string) => [...queryKeys.reports.all, 'view', id] as const,
    lock: (id: string) => [...queryKeys.reports.all, 'lock', id] as const,
    draftCount: () => [...queryKeys.reports.all, 'draft-count'] as const,
  },

  /**
   * Actions domain - Action items and workflow tasks
   */
  actions: {
    all: ['actions'] as const,
    byCompany: (companyId: string) => [...queryKeys.actions.all, 'company', companyId] as const,
    bySite: (companyId: string, siteId: string) =>
      [...queryKeys.actions.all, 'company', companyId, 'site', siteId] as const,
    byReport: (reportId: string) => [...queryKeys.actions.all, 'report', reportId] as const,
  },

  /**
   * Template domain - Report templates
   */
  templates: {
    all: ['templates'] as const,
    default: () => [...queryKeys.templates.all, 'default'] as const,
    version: (id: string, version: string) =>
      [...queryKeys.templates.all, 'version', id, version] as const,
  },

  /**
   * Calendar domain - Calendar entries and critical items
   */
  calendar: {
    all: ['calendar'] as const,
    entries: (params?: { from?: Date; to?: Date; includeFinalizedReports?: boolean }) =>
      [...queryKeys.calendar.all, 'entries', params] as const,
    criticalItems: () => [...queryKeys.calendar.all, 'critical-items'] as const,
    unplannedInspections: () => [...queryKeys.calendar.all, 'unplanned-inspections'] as const,
  },

  /**
   * Planning domain - Site scheduling list
   */
  planning: {
    all: ['planning'] as const,
    list: () => [...queryKeys.planning.all, 'list'] as const,
  },

  /**
   * Settings domain - Application settings
   */
  settings: {
    all: ['settings'] as const,
  },

  /**
   * Sync domain - Conflicts and status
   */
  sync: {
    all: ['sync'] as const,
    contactConflicts: () => [...queryKeys.sync.all, 'contacts'] as const,
    queue: () => [...queryKeys.sync.all, 'queue'] as const,
  },

  /**
   * Working set domain - Offline cache status
   */
  workingSet: {
    all: ['workingSet'] as const,
    status: () => [...queryKeys.workingSet.all, 'status'] as const,
  },

  /**
   * Catalogs domain - Dropdown options and reference data
   */
  catalogs: {
    all: ['catalogs'] as const,
    options: (catalogId: string) => ['catalog-options', catalogId] as const,
  },

  /**
   * Component categories domain - Refrigeration unit component types
   */
  componentCategories: {
    all: ['componentCategories'] as const,
  },
} as const;
